import { callLambda } from './lambda.js';

// Claims a job for the caller through the job/claim lambda, which also adds them to its workflow. Throws
// with the lambda's own reason when it refuses: already claimed, someone else claimed it just now, or the
// caller isn't in the job's team.
export async function claimJob(token: string, jobId: string): Promise<void> {
  await jobLambda('job/claim', token, { jobId }, 'Unable to claim this job.');
}

// Hands a claimed job back to its pool (job/release): only its holder may, and only a job they claimed
export async function releaseJob(token: string, jobId: string): Promise<void> {
  await jobLambda('job/release', token, { jobId }, 'Unable to release this job.');
}

// Moves a job to someone who may hold it, or back to its pool with null (job/reassign): only someone who manages it may
export async function reassignJob(token: string, jobId: string, personId: string | null): Promise<void> {
  await jobLambda('job/reassign', token, { jobId, personId }, 'Unable to reassign this job.');
}

async function jobLambda(path: string, token: string, body: object, fallback: string) {
  const response = await callLambda(path, 'POST', token, JSON.stringify(body));
  if (response.res.code !== 200)
    throw new Error(typeof response.res.message === 'string' ? response.res.message : fallback);
}

// An open job of the company, as job/company-open lists it for someone who manages or oversees it
export interface CompanyJob {
  id: string;
  title: string;
  assignedTo: string | null;
  assignedToTeamId: string | null;
  claimedAt: string | null;
  createdAt: string | null;
  runTitle: string | null;
  // The viewer manages it (an admin, or its team reports to them), so they can reassign it
  manages: boolean;
  // Someone who reports to the viewer directly holds it; the viewer oversees it without managing it, unless they also do
  heldByReport: boolean;
  // Whole days since the job was created, and whether that is past the 7 days every job gets before it costs anything
  ageDays: number;
  overdue: boolean;
  // The job is in a run another company started, so that company is waiting on it (and it costs double)
  otherCompany: boolean;
  // false for a job given to one person alone, which can't be handed on (a Payment failed card for the card's owner)
  reassignable?: boolean;
  // Who it can be handed to, worked out by the server, which reads who holds a card's permission and a permission team's members
  eligibleHolderIds: string[];
}

// The open jobs the caller manages, or null when they manage none (the lambda's 403), so the list isn't shown at all
export async function loadCompanyJobs(token: string): Promise<CompanyJob[] | null> {
  const response = await callLambda('job/company-open', 'GET', token);
  if (response.res.code === 403) return null;
  if (response.res.code !== 200) {
    throw new Error(
      typeof response.res.message === 'string' ? response.res.message : "Unable to load the company's jobs.",
    );
  }
  return Array.isArray(response.res.message) ? (response.res.message as unknown as CompanyJob[]) : [];
}

type ClaimableJob = {
  name?: string | null;
  assignedTo?: string | null;
  assignedToTeamId?: string | null;
  status?: string;
};

// Only a data job's holder can read its records (company-data/job/read), so one in the company's pool is claimed first
const HOLDER_ONLY_JOBS = ['@ns/data-add-record', '@ns/data-select-record', '@ns/data-edit-record'];

// A team job, or a pool job only its holder can work: someone takes it before opening it. Any other company-pool job
// can be opened and worked by anyone in the company.
const takenBeforeOpening = (job: ClaimableJob) => !!job.assignedToTeamId || HOLDER_ONLY_JOBS.includes(job.name ?? '');

// A job waiting for someone in its team, or its company's pool, to claim it
export function isUnclaimed(job: ClaimableJob) {
  return !job.assignedTo && takenBeforeOpening(job) && !hasEnded(job);
}

// A job finished, or cancelled (its run ended, or its timeout passed)
const hasEnded = (job: { status?: string }) => job.status === 'COMPLETE' || job.status === 'CANCELLED';

// A job someone took from its team or pool, including one claimed in this session before its record catches up
export function isClaimed(job: ClaimableJob, claimedHere = false) {
  return takenBeforeOpening(job) && !hasEnded(job) && (!!job.assignedTo || claimedHere);
}

// A team job another member of the team has claimed: the viewer sees who has it, and can neither claim nor open it. The
// viewer's own claim (any of their profiles, or one made in this session) is an ordinary assigned job.
export function isClaimedByOther(job: ClaimableJob, myPersonIds: readonly string[], claimedHere = false) {
  return !claimedHere && isClaimed(job) && !myPersonIds.includes(job.assignedTo ?? '');
}

// A job the viewer holds because they claimed it, which they can hand back; one given to them waits for a reassignment
export function isReleasable(
  job: { assignedTo?: string | null; claimedAt?: string | null; status?: string },
  myPersonIds: readonly string[],
) {
  return job.status === 'PENDING' && !!job.claimedAt && !!job.assignedTo && myPersonIds.includes(job.assignedTo);
}

// A job in a pool or with one to go back to: its team's, or the company's it waits in or was claimed from
export function hasPool(job: Pick<CompanyJob, 'assignedTo' | 'assignedToTeamId' | 'claimedAt'>) {
  return !!job.assignedToTeamId || !!job.claimedAt || !job.assignedTo;
}

// One holder's overdue jobs: a person's (personId), or, for jobs no one holds, a team's (teamId) or the company's pool
// (both null)
export interface OverdueGroup {
  personId: string | null;
  teamId: string | null;
  jobs: CompanyJob[];
}

// The overdue jobs grouped by who holds them, for an admin to chase. People come first, then teams, then the pool, and
// each group (and each group's jobs) oldest first. There is no total per group: the cost is the company's, never a person's.
export function overdueByHolder(jobs: readonly CompanyJob[]): OverdueGroup[] {
  const groups = new Map<string, OverdueGroup>();
  const oldestFirst = jobs.filter((job) => job.overdue).sort((a, b) => b.ageDays - a.ageDays);
  for (const job of oldestFirst) {
    const personId = job.assignedTo ?? null;
    const teamId = personId ? null : (job.assignedToTeamId ?? null);
    const key = personId ? `person:${personId}` : teamId ? `team:${teamId}` : 'pool';
    const group = groups.get(key) ?? { personId, teamId, jobs: [] };
    group.jobs.push(job);
    groups.set(key, group);
  }
  const rank = (group: OverdueGroup) => (group.personId ? 0 : group.teamId ? 1 : 2);
  // Map keeps insertion order, so groups of one rank stay oldest job first
  return [...groups.values()].sort((a, b) => rank(a) - rank(b));
}

// The overdue jobs a viewer sees, in two lists, each null when it isn't shown, so no job is in both. team: the jobs in a team
// (assignedToTeamId set) the viewer manages: every one for an admin, their teams' for a team's manager; shown to an admin, to
// anyone managing an open team job, and when there are no open jobs at all (so a team's manager still sees it empty).
// individual: the jobs in no team the viewer manages (an admin's), and every job held by someone who reports to the viewer
// directly that isn't already in the team list (one in no team, or in a team that doesn't report to the viewer); shown to an
// admin, and to anyone with an open job of that kind.
export function overdueSections(
  jobs: readonly CompanyJob[],
  admin: boolean,
): { team: CompanyJob[] | null; individual: CompanyJob[] | null } {
  const inTeamList = (job: CompanyJob) => !!job.assignedToTeamId && job.manages;
  const teamJobs = jobs.filter(inTeamList);
  const individualJobs = jobs.filter((job) => !inTeamList(job) && (job.manages || job.heldByReport));
  const overdue = (list: CompanyJob[]) => list.filter((job) => job.overdue);
  return {
    team: admin || jobs.length === 0 || teamJobs.length > 0 ? overdue(teamJobs) : null,
    individual: admin || individualJobs.length > 0 ? overdue(individualJobs) : null,
  };
}
